import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import { useHistory } from 'react-router-dom';
import AuthorCard from './AuthorCard/AuthorCard';
import css from './UsersPanel.module.css';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const PER_PAGE = 50;

const UsersPanel = props => {
  const {
    authors = {},
    loading = false,
    error = null,
  } = props;
  const history = useHistory();
  const location = useLocation();
  // ==========================================
  // FIX AUTHORS RESPONSE
  // ==========================================
  const authorsList = useMemo(() => {
    if (Array.isArray(authors)) return authors;

    if (Array.isArray(authors?.authors)) {
      return authors.authors;
    }

    if (authors && typeof authors === 'object') {
      return Object.keys(authors)
        .filter(key => !isNaN(Number(key)))
        .map(key => authors[key]);
    }

    return [];
  }, [authors]);

  // ==========================================
  // PAGE FROM URL
  // ==========================================
  const params = new URLSearchParams(location?.search || '');
  const urlPage = Number(params.get('page')) || 1;
  const [searchTerm, setSearchTerm] = useState('');
  // ==========================================
  // SEARCH (FRONTEND ONLY)
  // ==========================================
  const filteredAuthors = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return authorsList;

    return authorsList.filter(author => {
      const name =
        author?.attributes?.profile?.displayName || '';

      return name.toLowerCase().includes(term);
    });
  }, [authorsList, searchTerm]);

  // ==========================================
  // PAGINATION BASED ON AUTHORS LENGTH ONLY
  // ==========================================
  const totalPages =
    authors?.pagination?.totalPages || 1;

  const currentPage =
    authors?.pagination?.page || urlPage || 1;

  const visibleAuthors = filteredAuthors;


  // ==========================================
  // PAGE CHANGE
  // ==========================================
  const goToPage = page => {
    const nextPage = Math.max(
      1,
      Math.min(page, totalPages)
    );

    const currentSearch = location?.search || '';

    const search = currentSearch.replace(
      /page=[^&]*/g,
      `page=${nextPage}`
    );

    const finalSearch = search.includes('page=')
      ? search
      : `${search}${search.includes('?') ? '&' : '?'}page=${nextPage}`;

    history.push({
      pathname: location?.pathname || '/s',
      search: finalSearch,
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // ==========================================
  // SEARCH
  // ==========================================
  const onSearch = e => {
    setSearchTerm(e.target.value);
  };

  // reset page on search
  useEffect(() => {
    if (urlPage !== 1) {
      goToPage(1);
    }
  }, [searchTerm]);

  // ==========================================
  // STATES
  // ==========================================
  if (loading) {
    return (
      <div className={css.usersPanel}>
        <div className={css.loadingMessage}>
          Mitglieder werden geladen...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.usersPanel}>
        <div className={css.errorMessage}>
          Fehler beim Laden.
        </div>
      </div>
    );
  }

  return (
    <div className={css.usersPanel}>

      {/* HEADER */}
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage
            id="UsersPanel.membersWithActiveListings"
            values={{
              count: filteredAuthors.length,
            }}
          />
        </h2>
      </div>

      {/* USERS */}
      {visibleAuthors.length === 0 ? (
        <div className={css.emptyMessage}>
          Keine Mitglieder gefunden.
        </div>
      ) : (
        <div className={css.authorsGrid}>
          {visibleAuthors.map(author => (
            <AuthorCard
              key={author?.id?.uuid || author?.id}
              author={author}
            />
          ))}
        </div>
      )}

      {/* PAGINATION */}
      <div className={css.pagination}>
        <button
          className={css.paginationButton}
          disabled={currentPage <= 1}
          onClick={() => goToPage(currentPage - 1)}
        >
          ← Zurück
        </button>

        <span className={css.paginationInfo}>
          Seite {currentPage} / {totalPages}
        </span>

        <button
          className={css.paginationButton}
          disabled={currentPage >= totalPages}
          onClick={() => goToPage(currentPage + 1)}
        >
          Weiter →
        </button>
      </div>
    </div>
  );
};

UsersPanel.propTypes = {
  authors: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  loading: PropTypes.bool,
  error: PropTypes.string,
  location: PropTypes.object,
};

export default UsersPanel;